/**
 * Phase 4a token plumbing — first-party credential API client (WONE-565).
 *
 * Access token: in JS memory only. Wiped on tab close / hard refresh.
 * Refresh token: HttpOnly cookie ``__Secure-wone_refresh`` set by Platform,
 * never touched by JS. Bootstrap on mount calls ``/api/v1/auth/refresh`` to
 * mint a fresh access token if the cookie is present.
 *
 * Concurrent refresh callers are serialised by a promise mutex so multiple
 * Apollo queries hitting 401 at the same time only spend one rotation.
 * (Multi-tab coordination via ``navigator.locks`` is a follow-up — per-tab
 * single-flight is enough for the immediate need.)
 */
import constants from "Constant";
import { logError } from "utility/Function";
import {
  attemptDesktopRefresh,
  beginDesktopSSO,
  exchangeDesktopCode,
  forgetDesktopSsoSession,
  isDesktopPkce,
  signOutDesktopSession,
} from "desktop/sso";
import { getDesktopBridge } from "desktop/bridge";

import { randomUrlSafe, sha256B64Url } from "./pkce";

const CLIENT_ID = "wone-web";

export const PLATFORM_ORIGIN: string = (() => {
  try {
    return new URL(constants.API_URL).origin;
  } catch {
    return "";
  }
})();

let accessToken: string | null = null;

/**
 * Sentinel flag for the localStorage-based auth-gate checks in
 * user/index.js + company/index.tsx. The access token itself stays
 * in memory; this flag is just a "session present" boolean that
 * survives full-page reloads while bootstrap re-mints the access
 * token from the HttpOnly refresh cookie.
 */
const AUTHED_FLAG_KEY = "Authed";

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) {
    try {
      localStorage.setItem(AUTHED_FLAG_KEY, "1");
    } catch (err) {
      // QuotaExceeded / private-mode Safari / extension-blocked storage.
      // Not fatal — the in-memory access token still works — but we
      // want to know if it starts happening at scale.
      logError("Failed to set Authed flag in localStorage", err as Error);
    }
  } else {
    try {
      localStorage.removeItem(AUTHED_FLAG_KEY);
    } catch (err) {
      logError("Failed to clear Authed flag in localStorage", err as Error);
    }
  }
}

export function clearAccessToken(): void {
  accessToken = null;
  try {
    localStorage.removeItem(AUTHED_FLAG_KEY);
  } catch (err) {
    logError("Failed to clear Authed flag in localStorage", err as Error);
  }
}

/**
 * Whether this browser may hold a session worth recovering: the "Authed"
 * sentinel a sign-in left behind, or a desktop shell whose keychain can hold
 * a refresh token the sentinel knows nothing about. Unreadable storage counts
 * as no hint. A refresh that fails before a sign-in ever happened has nothing
 * to recover, so the boot gate can resolve it to sign-in silently.
 */
export function hasSessionHint(): boolean {
  if (getDesktopBridge()?.secureStorage !== undefined) return true;
  try {
    return localStorage.getItem(AUTHED_FLAG_KEY) !== null;
  } catch {
    return false;
  }
}

type CredentialResponse = {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  scope: string;
};

let refreshInFlight: Promise<RefreshOutcome> | null = null;

// Bounded retry for the refresh call. A refresh whose response is lost
// in transit (e.g. the full-page Zoom /live/ round-trip on flaky
// connectivity) still rotates the pair server-side, so without a retry
// the next 401 dead-ends in authenticationFailed() (alert + reload).
// Platform makes re-presenting a just-rotated token idempotent within
// the refresh grace window (returns the same successor), so retrying
// recovers the rotated pair instead of forcing re-login.
//
// Retry only transient failures — network errors (fetch throws) and 5xx.
// 401/403 is definitive (cookie genuinely gone) and clears the session;
// other non-ok (e.g. 404 partial rollout) keeps the session but isn't
// retried.
const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_RETRY_BASE_MS = 300;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Matches mobile's FETCH_TIMEOUT_MS (ApolloClient.tsx) — a hung-socket
// detector, not a latency budget (prod p95/p99 on this class of request
// is low single-digit seconds). Bare `fetch()` never resolves on some
// dropped-mid-flight CORS preflights, which otherwise wedges the boot
// probe forever.
const AUTH_FETCH_TIMEOUT_MS = 10_000;

const fetchWithTimeout = (
  input: string,
  init: RequestInit,
  timeoutMs = AUTH_FETCH_TIMEOUT_MS
): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(input, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
};

export type RefreshOutcome =
  | { kind: "ok"; token: string | null }
  | { kind: "terminal" } // 401/403 — session cleared, don't retry
  | { kind: "keep" } // non-ok, non-auth (e.g. 404) — keep session, don't retry
  | { kind: "transient" }; // network throw, timeout, or 5xx — retry

const attemptRefresh = async (): Promise<RefreshOutcome> => {
  // A desktop SSO session holds its refresh token in the shell's keychain,
  // not in a cookie, so it is tried first. Only a definitive rejection falls
  // through to the cookie, which then answers for itself; a transient failure
  // is reported as one rather than risking a sign-out.
  const desktop = await attemptDesktopRefresh({
    bridge: getDesktopBridge(),
    platformOrigin: PLATFORM_ORIGIN,
    fetchImpl: fetchWithTimeout,
  });
  if (desktop !== null && desktop.kind !== "terminal") {
    if (desktop.kind === "ok") {
      setAccessToken(desktop.token);
      return { kind: "ok", token: accessToken };
    }
    return desktop;
  }

  let res: Response;
  try {
    res = await fetchWithTimeout(`${PLATFORM_ORIGIN}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ client_id: CLIENT_ID }),
    });
  } catch {
    // Transport failure (offline, DNS, TLS, dropped mid-request) or our
    // own timeout abort.
    return { kind: "transient" };
  }
  if (res.status === 401 || res.status === 403) {
    // Refresh cookie definitively rejected — clear the "Authed"
    // sentinel so the gate doesn't stick on a stale flag from a
    // previous (now-revoked) session.
    setAccessToken(null);
    return { kind: "terminal" };
  }
  if (res.status >= 500) {
    return { kind: "transient" };
  }
  if (!res.ok) {
    // 404 / other 4xx — don't kill the session over a partial rollout,
    // but retrying won't help either.
    return { kind: "keep" };
  }
  const data: CredentialResponse = await res.json();
  setAccessToken(data.access_token);
  return { kind: "ok", token: accessToken };
};

const runRefresh = (
  maxAttempts: number = REFRESH_MAX_ATTEMPTS
): Promise<RefreshOutcome> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (attempt > 0) {
          await delay(REFRESH_RETRY_BASE_MS * 2 ** (attempt - 1));
        }
        const outcome = await attemptRefresh();
        if (outcome.kind !== "transient") return outcome;
        // transient — fall through to the next attempt
      }
      // Retries exhausted on a transient failure. Keep the session
      // (cookie + Authed flag) intact so a later refresh can recover.
      return { kind: "transient" };
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

export function refreshAccessToken(): Promise<string | null> {
  return runRefresh().then((outcome) =>
    outcome.kind === "ok" ? outcome.token : null
  );
}

function tokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * The in-memory access token, but only while it has over a minute left
 * before expiry — null means callers should refresh before using it.
 */
export function getAccessTokenIfFresh(): string | null {
  const token = accessToken;
  if (!token) {
    return null;
  }
  const expiry = tokenExpiryMs(token);
  return expiry === null || expiry - Date.now() > 60_000 ? token : null;
}

/**
 * Current access token, proactively refreshed when it is missing or within
 * a minute of expiry. For callers that attach the token to out-of-band
 * sends (e.g. WebSocket frames) where a 401-triggered retry isn't possible.
 */
export async function getFreshAccessToken(): Promise<string | null> {
  return getAccessTokenIfFresh() ?? refreshAccessToken();
}

// Unlike refreshAccessToken (used by callers that only care whether a
// token came back), the boot path needs to tell a definitively
// unrecoverable session (terminal — route to sign-in) apart from a
// transient failure (route to an error+retry state) instead of
// collapsing both to `null`.
//
// `maxAttempts` lets a boot with no session hint spend one attempt rather
// than the full retry budget: a browser that never signed in has nothing
// to recover, and one attempt still serves a cookie whose sentinel went
// missing.
export function bootstrapSession(options?: {
  maxAttempts?: number;
}): Promise<RefreshOutcome> {
  return runRefresh(options?.maxAttempts);
}

/**
 * Takes up the session an OTP or password sign-in returned. Inside the desktop
 * shell that first drops any stored SSO refresh token, which the boot refresh
 * would otherwise prefer over this sign-in's cookie. If that cannot be done
 * the sign-in fails rather than leaving the other session to come back.
 */
async function startCredentialSession(data: CredentialResponse): Promise<void> {
  await forgetDesktopSsoSession(getDesktopBridge());
  setAccessToken(data.access_token);
}

/**
 * Like getFreshAccessToken, but preserves the refresh outcome so callers
 * deciding whether to send at all (e.g. fire-and-forget analytics
 * mutations) can tell a dead session (terminal — re-auth) apart from a
 * transient failure (skip and carry on).
 */
export function ensureFreshSession(): Promise<RefreshOutcome> {
  const token = getAccessTokenIfFresh();
  if (token) return Promise.resolve({ kind: "ok", token });
  return runRefresh();
}

// WONE-589: requestMagicLink, requestPasswordReset, redeemPasswordReset
// helpers removed. The unified flow doesn't reach them and the screens
// they backed (sign-in-magic-link, reset-password, change-password) were
// deleted in the same commit. Restore via `git show HEAD~1` if a
// password/magic-link surface needs to come back.

// ---------------------------------------------------------------------
// Email OTP (WONE-612 — unified email-first flow)
// ---------------------------------------------------------------------
//
// Anti-enumeration: ``request`` always 200s on a well-formed input. We
// only throw on transport failures or 5xx so the caller doesn't show a
// false "code sent" screen when Platform is down. The server returns a
// ``cooldown_seconds`` value so the resend timer is server-driven —
// duplicate Customer.io dispatches are deduped by the same window.

export type OtpPurpose = "login" | "signup";

type OtpRequestResponse = { detail?: string; cooldown_seconds: number };

export class OtpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "OtpError";
  }
}

export async function requestEmailOtp(
  purpose: OtpPurpose,
  email: string
): Promise<OtpRequestResponse> {
  const res = await fetch(
    `${PLATFORM_ORIGIN}/api/v1/auth/otp/${purpose}/request`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ client_id: CLIENT_ID, email }),
    }
  );
  if (!res.ok) {
    throw new OtpError("otp request failed", res.status);
  }
  const data = (await res.json()) as Partial<OtpRequestResponse>;
  if (typeof data.cooldown_seconds !== "number") {
    // Server is expected to return the remaining cooldown window so we
    // can drive the resend timer without a client-side fallback. A
    // missing value means a contract mismatch worth surfacing rather
    // than silently swallowing.
    throw new OtpError("otp response missing cooldown_seconds", 500);
  }
  return data as OtpRequestResponse;
}

/**
 * Verify a login OTP. On 200 the server mints tokens (HttpOnly cookie +
 * access token in body) and we store the access token. Errors are
 * surfaced via ``OtpError.status`` so callers can render the right
 * inline copy (401 = wrong code, 429 = too many attempts).
 */
export async function verifyLoginOtp(args: {
  email: string;
  code: string;
}): Promise<void> {
  const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/auth/otp/login/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ client_id: CLIENT_ID, ...args }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new OtpError(body.detail || "otp verify failed", res.status);
  }
  const data: CredentialResponse = await res.json();
  await startCredentialSession(data);
}

/**
 * Sign in with email + password (accounts with the ``password_auth``
 * flag). Same mechanics as login verify; 401 = invalid credentials
 * (wrong password or flag off — Platform doesn't distinguish), 429 =
 * throttled. Surfaced via ``OtpError.status``.
 */
export async function loginWithPassword(args: {
  email: string;
  password: string;
}): Promise<void> {
  const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ client_id: CLIENT_ID, ...args }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new OtpError(body.detail || "password login failed", res.status);
  }
  const data: CredentialResponse = await res.json();
  await startCredentialSession(data);
}

/**
 * Verify a signup OTP. Same mechanics as login verify, plus the user's
 * name fields so Platform can finish provisioning the inactive user
 * created at /signup. Password is intentionally omitted — the unified
 * flow ditches the password step for OTP signup (server creates with an
 * unusable password; the user can set one later via reset).
 *
 * 409 means an account already exists for this email — the signup
 * happy-path collided with a concurrent sign-in (or the user picked the
 * wrong branch). Caller renders "Sign in instead".
 */
export async function verifySignupOtp(args: {
  email: string;
  code: string;
  firstName?: string;
  lastName?: string;
}): Promise<void> {
  const res = await fetch(`${PLATFORM_ORIGIN}/api/v1/auth/otp/signup/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      client_id: CLIENT_ID,
      email: args.email,
      code: args.code,
      // Mobile parity (PR #820): the unified flow doesn't collect
      // first/last name on the way in — the user fills them during
      // onboarding. Backend accepts empty strings and provisions the
      // account with those defaults.
      first_name: args.firstName ?? "",
      last_name: args.lastName ?? "",
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new OtpError(body.detail || "otp verify failed", res.status);
  }
  const data: CredentialResponse = await res.json();
  await startCredentialSession(data);
}

// ---------------------------------------------------------------------
// SSO via OAuth 2.1 Authorization Code + PKCE (WONE-565 Phase 4b-3)
// ---------------------------------------------------------------------

export const PKCE_SESSION_KEY = "wone-pkce";
const REDIRECT_PATH = "/auth/callback";
// Every scope here must be registered in Platform's OAUTH2_PROVIDER.SCOPES —
// AuthorizeScopeRegistrationTests in wone_auth/tests_oauth_adapter.py reads
// this list from the file and authorizes with it.
const SSO_SCOPES =
  "openid profile email learner ori:chat company-admin content:search knowledge:read readings:read readings:raw";

/**
 * Single source of truth for the SSO providers Platform routes through
 * /o/authorize?idp_hint. Importers (navigate-to-method, method-chooser)
 * derive their own predicates / icon / label maps off this type so
 * adding a provider is one edit here plus the icon/label entry.
 */
export type SsoProvider = "google-oauth2" | "azuread-oauth2" | "okta";

type PkceState = {
  code_verifier: string;
  state: string;
  return_to?: string;
  /**
   * WONE-713: the IdP the user picked at the chooser. Survives the
   * redirect round-trip so /auth/callback can attach ``Provider`` to
   * ``Auth SSO Completed`` / ``Auth SSO Error`` (per-IdP success rates).
   */
  provider?: SsoProvider;
};

/**
 * SSO-flow failure with a stable machine-readable ``code`` so analytics
 * and error reporting classify the real cause instead of re-deriving it
 * from the human-readable message (WONE-713 review; see also WONE-740).
 */
export class SsoFlowError extends Error {
  code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = "SsoFlowError";
    this.code = code;
  }
}

/**
 * Stable error code for an SSO-flow failure. Reads the ``code`` set at
 * the throw site (``SsoFlowError`` or any error carrying a string
 * ``code``); falls back to ``sso_failed`` for unclassified errors.
 */
export function ssoErrorCode(err: unknown): string {
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code?: unknown }).code;
    if (typeof code === "string") return code;
  }
  return "sso_failed";
}

/**
 * Read the in-flight SSO provider from the PKCE session state without
 * consuming it. Returns null when no SSO flow is in flight (deep-link
 * straight to /auth/callback, or storage cleared mid-flow).
 */
export function peekSSOProvider(): SsoProvider | null {
  const stored = sessionStorage.getItem(PKCE_SESSION_KEY);
  if (!stored) return null;
  try {
    return (JSON.parse(stored) as PkceState).provider ?? null;
  } catch {
    return null;
  }
}

function getRedirectUri(): string {
  return `${window.location.origin}${REDIRECT_PATH}`;
}

/**
 * Begin an SSO flow. Generates a fresh PKCE pair + state, stashes the
 * verifier in sessionStorage (per-tab; survives the IdP redirect), and
 * full-page-redirects to /o/authorize?idp_hint=<backend>. The IdP
 * round-trip lands back at /auth/callback with ``code=...&state=...``.
 */
export async function beginSSO(
  idpHint: SsoProvider,
  returnTo?: string,
  loginHint?: string
): Promise<void> {
  const bridge = getDesktopBridge();
  if (bridge !== null) {
    // The desktop shell signs in through the system browser and a deep link,
    // never by navigating its own window to the identity provider.
    await beginDesktopSSO({
      bridge,
      platformOrigin: PLATFORM_ORIGIN,
      pkceSessionKey: PKCE_SESSION_KEY,
      idpHint,
      returnTo,
      loginHint,
    });
    return;
  }

  const codeVerifier = randomUrlSafe(32);
  const state = randomUrlSafe(16);
  const codeChallenge = await sha256B64Url(codeVerifier);

  const pkce: PkceState = {
    code_verifier: codeVerifier,
    state,
    return_to: returnTo,
    provider: idpHint,
  };
  sessionStorage.setItem(PKCE_SESSION_KEY, JSON.stringify(pkce));

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: getRedirectUri(),
    scope: SSO_SCOPES,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    idp_hint: idpHint,
  });
  // Okta uses login_hint for per-company tenant routing on Platform.
  // Google and Microsoft accept it too (pre-fills the email field).
  if (loginHint) {
    params.set("login_hint", loginHint);
  }
  window.location.assign(`${PLATFORM_ORIGIN}/o/authorize/?${params.toString()}`);
}

/**
 * Exchange the authorization ``code`` for tokens at /o/token. Platform's
 * WebRefreshCookieTokenView intercepts wone-web responses to set the
 * ``__Secure-wone_refresh`` cookie server-side and strip refresh_token
 * from the body, so we only see ``access_token`` here. Returns the
 * sessionStorage ``return_to`` URL if one was captured at SSO start.
 */
export async function completeSSO(
  code: string,
  state: string
): Promise<{ accessToken: string; returnTo?: string }> {
  const stored = sessionStorage.getItem(PKCE_SESSION_KEY);
  if (!stored) {
    throw new SsoFlowError("SSO session not found", "sso_session_missing");
  }
  const parsed: unknown = JSON.parse(stored);

  if (isDesktopPkce(parsed)) {
    // A desktop callback arrives as a custom-scheme link, which any web page
    // can fire. So a mismatched state leaves the in-flight session in place:
    // consuming it would let such a link break the real sign-in.
    if (parsed.state !== state) {
      throw new SsoFlowError("SSO state mismatch", "state_mismatch");
    }
    sessionStorage.removeItem(PKCE_SESSION_KEY);
    const bridge = getDesktopBridge();
    if (bridge === null) {
      throw new SsoFlowError("desktop SSO callback outside the desktop shell", "desktop_bridge_missing");
    }
    const { accessToken: token } = await exchangeDesktopCode({
      bridge,
      platformOrigin: PLATFORM_ORIGIN,
      pkce: parsed,
      code,
      fetchImpl: fetchWithTimeout,
    });
    setAccessToken(token);
    return { accessToken: token, returnTo: parsed.return_to };
  }

  sessionStorage.removeItem(PKCE_SESSION_KEY);
  const pkce = parsed as PkceState;
  if (pkce.state !== state) {
    throw new SsoFlowError("SSO state mismatch", "state_mismatch");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    client_id: CLIENT_ID,
    redirect_uri: getRedirectUri(),
    code_verifier: pkce.code_verifier,
  });

  const res = await fetch(`${PLATFORM_ORIGIN}/o/token/`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    credentials: "include",
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new SsoFlowError(
      `SSO token exchange failed: ${res.status} ${text}`,
      "token_exchange_failed"
    );
  }
  const data: CredentialResponse = await res.json();
  setAccessToken(data.access_token);
  return { accessToken: data.access_token, returnTo: pkce.return_to };
}

/**
 * Look up the authenticated user's identity (WONE-569).
 *
 * Three-state outcome so callers can react correctly:
 *
 *   - ``ok``       — identity returned; safe to populate user state.
 *   - ``rejected`` — server refused the bearer (401/403). The token
 *                    is stale; caller should clear it.
 *   - ``unavailable`` — transport-level failure (network, 5xx, 404,
 *                    or any other non-2xx that isn't 401/403). Caller
 *                    should leave the existing session intact and
 *                    skip the profile load — a Platform partial
 *                    rollout (no /me yet) or a transient hiccup must
 *                    not log users out.
 *
 * Replaces the legacy ``localStorage.userEmailId`` cache as the source
 * of truth — clients call this after credential issuance and on cold
 * start to populate user state without depending on a previously
 * cached email.
 */
export type FetchMeResult =
  | { status: "ok"; user: { id: number; email: string } }
  | { status: "rejected" }
  | { status: "unavailable" };

export async function fetchMe(): Promise<FetchMeResult> {
  const token = accessToken;
  if (!token) return { status: "rejected" };
  let res: Response;
  try {
    res = await fetchWithTimeout(`${PLATFORM_ORIGIN}/api/v1/auth/me`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
  } catch {
    // Network failure (offline, DNS, CORS preflight rejected) or our own
    // timeout abort. The bearer is presumed valid; only the transport
    // failed.
    return { status: "unavailable" };
  }
  if (res.status === 401 || res.status === 403) {
    return { status: "rejected" };
  }
  if (!res.ok) {
    // 5xx / 404 / etc. Treat as transient — don't kill the session.
    return { status: "unavailable" };
  }
  const user = (await res.json()) as { id: number; email: string };
  return { status: "ok", user };
}

export async function revokeSession(): Promise<void> {
  // A desktop sign-out that could not clear the keychain still revokes the
  // cookie session and clears local state below, then reports the failure.
  let desktopSignOutError: unknown = null;
  try {
    try {
      await signOutDesktopSession({
        bridge: getDesktopBridge(),
        platformOrigin: PLATFORM_ORIGIN,
      });
    } catch (err) {
      desktopSignOutError = err;
    }
    await fetch(`${PLATFORM_ORIGIN}/api/v1/auth/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ client_id: CLIENT_ID }),
    });
  } finally {
    accessToken = null;
    try {
      localStorage.removeItem(AUTHED_FLAG_KEY);
    } catch (err) {
      logError("Failed to clear Authed flag in localStorage", err as Error);
    }
  }
  if (desktopSignOutError !== null) throw desktopSignOutError;
}
